import React from 'react';
import { AlertTriangle } from 'lucide-react';

class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Uncaught render error:', error, info);
    this.setState({ stack: (info && info.componentStack) || '' });
  }

  // The details are on the page, not only in the console. "An unexpected error occurred" is a dead
  // end for everyone: the person seeing it cannot say what broke, and whoever they report it to
  // cannot act on it. One click gives them a message and the component that threw, and a Copy
  // button so it can be pasted into a message instead of retyped from a screenshot.
  copy = () => {
    const { error, stack } = this.state;
    const text = `${(error && (error.stack || error.message)) || String(error)}\n\nComponent stack:${stack || ' (unavailable)'}\n\nURL: ${window.location.href}`;
    try {
      navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch { /* clipboard blocked — the text is on screen to select by hand */ }
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm max-w-xl w-full p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle size={26} className="text-red-500" />
            </div>
            <h1 className="text-lg font-bold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-500 mb-6">
              An unexpected error occurred. Try reloading the page — if it keeps happening, head back to the dashboard.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors">
                Reload
              </button>
              <a
                href="/"
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Go to dashboard
              </a>
            </div>

            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-slate-700 select-none">
                Show details
              </summary>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400">Paste this when reporting the problem.</span>
                <button type="button" onClick={this.copy}
                  className="px-2 py-1 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                  {this.state.copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-slate-900 text-slate-100 text-[11px] leading-relaxed p-3 whitespace-pre-wrap break-words">
{(this.state.error && (this.state.error.stack || this.state.error.message)) || String(this.state.error)}
{this.state.stack ? `\n\nComponent stack:${this.state.stack}` : ''}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
